"use client";

import React from "react";
import { Star, Quote } from "lucide-react";

/**
 * Status Badge Component
 * Spec: Inline-flex container with 1px border (#1A3C2B/20). Inside: 8px x 8px square dot of solid #1A3C2B
 * followed by JetBrains Mono text at 10px, uppercase, tracking-widest. Padding: 4px 12px.
 */
export function StatusBadge({ text = "SYSTEM OPERATIONAL // VER 4.2.0" }: { text?: string }) {
  return (
    <div className="inline-flex items-center gap-2 border border-[#1A3C2B]/20 bg-[#1A3C2B]/5 px-3 py-1 font-mono-tech text-[10px] uppercase tracking-widest text-[#1A3C2B] rounded-none">
      <div className="w-2 h-2 bg-[#1A3C2B] rounded-none shrink-0" />
      <span>{text}</span>
    </div>
  );
}

/**
 * Network Topology Graph Component
 * Spec: Circular container (max-width: 450px) with 1px border. Central 16px solid forest green node.
 * Three orbiting nodes on dashed paths (140px radius). Connect nodes to center using 1px solid lines
 * at 0.2 opacity. Animate orbiting nodes with a linear 20s infinite rotation.
 */
export function NetworkTopologyGraph() {
  return (
    <div className="relative w-full max-w-[450px] aspect-square rounded-full border border-[#3A3A38]/20 bg-[#F7F7F5] flex items-center justify-center p-6 mx-auto overflow-hidden">
      {/* Central 16px solid forest green node */}
      <div className="w-4 h-4 bg-[#1A3C2B] rounded-full z-20 shadow-[0_0_0_8px_rgba(26,60,43,0.15)] flex items-center justify-center">
        <div className="w-1.5 h-1.5 bg-white rounded-full" />
      </div>

      {/* Outer Dashed Orbit Track (140px radius = 280px diameter) */}
      <div className="absolute w-[280px] h-[280px] rounded-full border border-dashed border-[#3A3A38]/40 z-10 pointer-events-none" />

      {/* Orbiting Container with linear 20s infinite rotation */}
      <div className="absolute inset-0 w-full h-full animate-spin-linear z-10 pointer-events-none">
        {/* Node 1 - Top (0 deg) */}
        <div className="absolute top-[85px] left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
          <div className="w-3 h-3 bg-[#FF8C69] border border-[#1A3C2B] rounded-none" />
          <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[#1A3C2B] mt-1 bg-[#F7F7F5] px-1 border border-[#3A3A38]/20">
            TALENT_RADAR
          </span>
        </div>

        {/* Node 2 - Bottom Right (120 deg) */}
        <div className="absolute bottom-[105px] right-[100px] flex flex-col items-center">
          <div className="w-3 h-3 bg-[#9EFFBF] border border-[#1A3C2B] rounded-none" />
          <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[#1A3C2B] mt-1 bg-[#F7F7F5] px-1 border border-[#3A3A38]/20">
            BIOMETRICS_AI
          </span>
        </div>

        {/* Node 3 - Bottom Left (240 deg) */}
        <div className="absolute bottom-[105px] left-[100px] flex flex-col items-center">
          <div className="w-3 h-3 bg-[#F4D35E] border border-[#1A3C2B] rounded-none" />
          <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[#1A3C2B] mt-1 bg-[#F7F7F5] px-1 border border-[#3A3A38]/20">
            ERP_FACILITY
          </span>
        </div>
      </div>

      {/* Connecting Lines (1px solid lines at 0.2 opacity) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
        <line x1="50%" y1="50%" x2="50%" y2="20%" stroke="#3A3A38" strokeWidth="1" strokeOpacity="0.2" />
        <line x1="50%" y1="50%" x2="78%" y2="72%" stroke="#3A3A38" strokeWidth="1" strokeOpacity="0.2" />
        <line x1="50%" y1="50%" x2="22%" y2="72%" stroke="#3A3A38" strokeWidth="1" strokeOpacity="0.2" />
      </svg>
    </div>
  );
}

/**
 * Monospaced Testimonial Card Component
 * Spec: 1px bordered square card. Top: Quote icon and star ratings (5 icons, 10px each).
 * Body: Monospaced text at 12px, line-height 1.6. Bottom: 1px top border with user avatar
 * (square, no radius) and name/title in JetBrains Mono.
 */
export function MonospacedTestimonialCard({
  quote,
  author,
  role,
  school,
  avatarUrl = "/media/olympion_logo.jpeg",
}: {
  quote: string;
  author: string;
  role: string;
  school: string;
  avatarUrl?: string;
}) {
  return (
    <div className="border border-[#3A3A38]/20 bg-[#F7F7F5] p-6 rounded-none flex flex-col justify-between space-y-4">
      <div className="space-y-3">
        {/* Top Header with Quote icon and Star ratings */}
        <div className="flex items-center justify-between">
          <Quote className="w-4 h-4 text-[#1A3C2B]" />
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-2.5 h-2.5 fill-[#1A3C2B] text-[#1A3C2B]" />
            ))}
          </div>
        </div>

        {/* Body Text */}
        <p className="font-mono-tech text-xs text-[#3A3A38] leading-[1.6]">
          "{quote}"
        </p>
      </div>

      {/* Bottom User Info with 1px top border */}
      <div className="border-t border-[#3A3A38]/20 pt-4 flex items-center gap-3">
        <div className="w-9 h-9 border border-[#3A3A38]/30 rounded-none overflow-hidden shrink-0 bg-white">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={avatarUrl} alt={author} className="w-full h-full object-cover img-luminosity" />
        </div>
        <div>
          <h4 className="font-mono-tech text-xs font-bold uppercase tracking-wider text-[#1A3C2B]">
            {author}
          </h4>
          <p className="font-mono-tech text-[10px] text-[#3A3A38]/70 uppercase tracking-tight">
            {role} // {school}
          </p>
        </div>
      </div>
    </div>
  );
}
